import { useState, useEffect } from 'react';
import { Input, Col, Tooltip } from 'antd';
import useMeterReadDetailsTableMonthCell from './useMeterReadDetailsTableMonthCell';
import MeterReadDetailsFileCell from './MeterReadDetailsFileCell';

const isKwhField = (label?: string) =>
  !!label && /kwh/i.test(label);

// Round value for display in kWh/noDecimals fields
const getRoundedDisplayValue = (value: any, label?: string, noDecimals?: boolean) => {
  if (value == null || value === '') return '';
  if (noDecimals || isKwhField(label)) {
    const num = Number(value);
    if (Number.isFinite(num)) return String(Math.round(num));
  }
  return String(value);
};

const formatDisplayValue = (value: any, label?: string, noDecimals?: boolean) => {
  if (value == null || value === '' || value === '--') return value ?? '--';
  if (noDecimals || isKwhField(label)) {
    const num = Number(value);
    if (Number.isFinite(num)) return Math.round(num).toLocaleString();
  }
  const num = Number(value);
  if (Number.isFinite(num)) return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value;
};

const MeterReadDetailsTableMonthCell = ({
  id,
  editable,
  isEditable: isEditableProp,
  type = 'number',
  data,
  onChange,
  saving,
  updating,
  styles,
  label,
  noDecimals,
  retroInput = false,
}) => {
  const { isEditable, isReadonly, } = useMeterReadDetailsTableMonthCell({
    id,
    data,
    editable,
    isEditable: isEditableProp,
    retroInput,
  });

  // Hooks must be called before any early returns
  // Use local state to track if user is actively editing (to allow decimal entry)
  // Display rounded value when not editing, raw value while editing
  const shouldRound = noDecimals || isKwhField(label);
  const [localValue, setLocalValue] = useState(() => getRoundedDisplayValue(data?.value, label, noDecimals));
  const [isEditing, setIsEditing] = useState(false);

  // Sync local value with data.value when data changes from server (after save)
  useEffect(() => {
    if (!isEditing && data) {
      setLocalValue(getRoundedDisplayValue(data.value, label, noDecimals));
    }
  }, [data, label, noDecimals, isEditing]);

  if (!data) return null;
  const shouldHighlightRate = data?.isDifferentFromPrevious === true && label === 'Rate';

  if (type === 'file_upload') {
    return (
      <MeterReadDetailsFileCell
        id={id}
        data={data}
        styles={styles}
      />
    );
  }

  if (isReadonly) {
    const cellClass = `${styles.cellValue} ${styles.readOnlyCell} ${shouldHighlightRate ? styles.rateChanged : ''}`;
    const displayValue = formatDisplayValue(data.value, label, noDecimals);
    const readOnlyTooltip = data.isApproved ? 'Approved — this field is locked' : 'Read-only data';
    return (
      <Tooltip title={readOnlyTooltip}>
        <Col className={cellClass}>{displayValue}</Col>
      </Tooltip>
    );
  }

  if (isEditable) {
    const handleBlur = () => {
      setIsEditing(false);
      if (shouldRound) setLocalValue(getRoundedDisplayValue(data.value, label, noDecimals));
    };
    return (
      <Input
        size="small"
        className={`${styles.inputCell} ${shouldHighlightRate ? styles.rateChanged : ''}`}
        value={localValue}
        onFocus={() => { setIsEditing(true); setLocalValue(data.value == null ? '' : String(data.value)); }}
        onBlur={handleBlur}
        onChange={(e) => { setLocalValue(e.target.value); onChange(e.target.value); }}
        disabled={saving || updating}
      />
    );
  }

  const displayValue = formatDisplayValue(data.value, label, noDecimals);
  const cellClassName = shouldHighlightRate ? `${styles.cellValue} ${styles.rateChanged}` : styles.cellValue;
  const cell = <Col className={cellClassName}>{displayValue}</Col>;

  return (label === 'Prod Stat Meter Read Type' && data.comment)
    ? <Tooltip title={data.comment}>{cell}</Tooltip>
    : cell;
};

export default MeterReadDetailsTableMonthCell;
